import React from 'react';
import DOMPurify from 'dompurify';

interface FormattingOptions {
  allowBold: boolean;
  allowItalic: boolean;
  sanitize: boolean;
}

const DEFAULT_OPTIONS: FormattingOptions = {
  allowBold: true,
  allowItalic: true,
  sanitize: true
};

interface TextSegment {
  type: 'text' | 'bold' | 'italic';
  content: string;
  key: string;
}

/**
 * Main entry point for message formatting
 * Parses markdown-style formatting and returns React nodes
 */
export function formatMessage(
  content: string, 
  options: FormattingOptions = DEFAULT_OPTIONS
): React.ReactNode[] {
  if (!content || typeof content !== 'string') {
    return [content];
  }

  // Sanitize content first if enabled
  const sanitizedContent = options.sanitize ? sanitizeHtml(content) : content;
  
  // Parse markdown patterns
  const segments = parseMarkdown(sanitizedContent, options);
  
  // Convert segments to React elements
  return segments.map(segment => createTextElement(segment));
}

/**
 * Parse markdown patterns in text and return structured segments
 */
export function parseMarkdown(text: string, options: FormattingOptions = DEFAULT_OPTIONS): TextSegment[] {
  if (!text) {
    return [{
      type: 'text',
      content: text,
      key: 'text-0'
    }];
  }

  const segments: TextSegment[] = [];
  let segmentCounter = 0;

  // Process bold first, then italic to handle precedence correctly
  const boldMatches: Array<{start: number, end: number, content: string}> = [];
  
  if (options.allowBold) {
    const boldRegex = /\*\*([\s\S]*?)\*\*/g; // Use [\s\S] to match including newlines
    let match: RegExpExecArray | null;
    while ((match = boldRegex.exec(text)) !== null) {
      boldMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        content: match[1]
      });
    }
  }

  const italicMatches: Array<{start: number, end: number, content: string}> = [];
  if (options.allowItalic) {
    const italicRegex = /(?<!\*)\*(?!\*)([\s\S]*?)\*(?!\*)/g; // Use [\s\S] to match including newlines
    let match: RegExpExecArray | null;
    while ((match = italicRegex.exec(text)) !== null) {
      // Skip if this italic is inside a bold section
      const matchIndex = match.index;
      const matchLength = match[0].length;
      const isInsideBold = boldMatches.some(bold => 
        matchIndex >= bold.start && matchIndex + matchLength <= bold.end
      );
      if (!isInsideBold) {
        italicMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          content: match[1]
        });
      }
    }
  }

  // Combine all matches and sort by position
  const allMatches = [
    ...boldMatches.map(m => ({...m, type: 'bold' as const})),
    ...italicMatches.map(m => ({...m, type: 'italic' as const}))
  ].sort((a, b) => a.start - b.start);

  // Build segments
  let currentPos = 0;
  for (const match of allMatches) {
    // Add text before match
    if (match.start > currentPos) {
      segments.push({
        type: 'text',
        content: text.slice(currentPos, match.start),
        key: `text-${segmentCounter++}`
      });
    }

    // Add the formatted segment
    segments.push({
      type: match.type,
      content: match.content,
      key: `${match.type}-${segmentCounter++}`
    });

    currentPos = match.end;
  }

  // Add remaining text
  if (currentPos < text.length) {
    segments.push({
      type: 'text',
      content: text.slice(currentPos),
      key: `text-${segmentCounter++}`
    });
  }

  // If no matches found, return the original text
  if (segments.length === 0) {
    return [{
      type: 'text',
      content: text,
      key: 'text-0'
    }];
  }

  return segments;
}

/**
 * Sanitize HTML content to prevent XSS attacks
 */
export function sanitizeHtml(html: string): string {
  // Configure DOMPurify to only allow safe formatting
  const cleanHtml = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [], // Don't allow any HTML tags in input
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true // Keep text content even if tags are removed
  });
  
  return cleanHtml;
}

/**
 * Create React elements from text segments
 */
function createTextElement(segment: TextSegment): React.ReactNode {
  switch (segment.type) {
    case 'bold':
      return React.createElement('strong', { key: segment.key }, processNewlines(segment.content));
    case 'italic':
      return React.createElement('em', { key: segment.key }, processNewlines(segment.content));
    case 'text':
    default:
      return processNewlines(segment.content, segment.key);
  }
}

/**
 * Process newlines in text content and convert to React elements
 */
function processNewlines(text: string, baseKey?: string): React.ReactNode | React.ReactNode[] {
  if (!text || typeof text !== 'string') {
    return text;
  }

  const lines = text.split('\n');
  
  if (lines.length === 1) {
    return text;
  }

  // Convert to array of text nodes and <br> elements
  const elements: React.ReactNode[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (i > 0) {
      elements.push(React.createElement('br', { key: `${baseKey || 'br'}-${i}` }));
    }
    if (lines[i]) { // Only add non-empty lines
      elements.push(lines[i]);
    }
  }

  return elements;
}

